// third party plugins
import React, { useMemo, useCallback } from "react";
import PropTypes from "prop-types";
//
import { useSelector } from "react-redux";

// constants
import { TOP_HIERRACY_CATEGORIES } from "constants.js";

// selectors
import {
  showSegmentFilterSelector,
  segmentFilterLabelSelector,
  fetchedDynamicFiltersSelector,
  appliedDynamicFiltersSelector,
} from "selectors/filtersSelector.js";
import { buyplanStateSelector } from "selectors/buyplanIdSelector.js";

// presentational components
import H5 from "components/H5.js";
import i18next from "i18next";

// utils
import { isFilterVisibleFn, getFilterDisplayName } from "../utils.js";
import { FilterHeader, FilterContent } from "../filterComponentsUtils.jsx";

const FiltersList = ({ onToggle, onFilterCategoryReset }) => {
  const dynamicFilters = useSelector(fetchedDynamicFiltersSelector);
  const filterSequence = dynamicFilters?.get("filter_sequence");
  const buyplanState = useSelector(buyplanStateSelector);
  const isQuickFill = buyplanState === "qfill";
  const showSegmentFilter = useSelector(showSegmentFilterSelector);
  const segmentFilterLabel = useSelector(segmentFilterLabelSelector);
  const appliedDynamicFilters = useSelector(appliedDynamicFiltersSelector);

  const categoryIndices = useMemo(() => {
    const indexOfCategory = filterSequence?.indexOf("filter_tags");
    const indexOfQfCategory = isQuickFill
      ? filterSequence?.indexOf("category4")
      : -1;
    const indexOfQfSegments = isQuickFill
      ? filterSequence?.indexOf("segments")
      : -1;

    let categoryIndex = false;
    if (indexOfCategory !== -1) categoryIndex = indexOfCategory;
    if (indexOfQfCategory !== -1) categoryIndex = indexOfQfCategory + 1;
    if (indexOfQfSegments !== -1) categoryIndex = indexOfQfSegments + 1;

    return { categoryIndex };
  }, [filterSequence, isQuickFill]);

  const handleFilterCategoryReset = useCallback(
    (e) => {
      onFilterCategoryReset(e);
    },
    [onFilterCategoryReset]
  );

  const renderFilter = useCallback(
    (filterName, index) => {
      if (TOP_HIERRACY_CATEGORIES.includes(filterName)) return null;

      const filterValue = dynamicFilters?.get(filterName);

      if (!isFilterVisibleFn(filterName, filterValue, showSegmentFilter)) {
        return null;
      }

      const filterDisplayName = getFilterDisplayName(
        filterName,
        segmentFilterLabel
      );

      return (
        <React.Fragment key={filterName}>
          {categoryIndices.categoryIndex === index && (
            <H5 className="mt-4 mb-4">{i18next.t("sidebar.refineBy")}</H5>
          )}

          <details open={filterName === "divisions"}>
            <summary>
              <FilterHeader filterDisplayName={filterDisplayName} />
            </summary>
            <FilterContent
              filterName={filterName}
              filterValue={filterValue}
              filterSequence={filterSequence}
              appliedDynamicFilters={appliedDynamicFilters}
              onToggle={onToggle}
              onFilterCategoryReset={handleFilterCategoryReset}
            />
          </details>
        </React.Fragment>
      );
    },
    [
      dynamicFilters,
      showSegmentFilter,
      segmentFilterLabel,
      filterSequence,
      appliedDynamicFilters,
      onToggle,
      handleFilterCategoryReset,
      categoryIndices,
    ]
  );

  const filters = useMemo(
    () =>
      filterSequence?.map((filterName, index) =>
        renderFilter(filterName, index)
      ),
    [filterSequence, renderFilter]
  );

  return filters;
};

FiltersList.propTypes = {
  onToggle: PropTypes.func.isRequired,
  onFilterCategoryReset: PropTypes.func.isRequired,
};

export default React.memo(FiltersList);
